import { useAtomValue } from 'jotai';

import { offlineConnectivityStateAtom } from '@/atoms/offline-recording';
import { isTauri } from '@/tauri/utils';

import { useNavigatorOnline } from './useNavigatorOnline';

/**
 * True when it is safe for desktop startup code to hit Heidi/EHR APIs.
 *
 * In Tauri dev, localhost can stay reachable while the internet/backend is not,
 * so `navigator.onLine` is not a reliable backend-readiness signal. Use the
 * Rust reachability monitor instead. Web keeps the existing behavior.
 *
 * Optimistic on `unknown`: a not-yet-armed probe (the atom's initial value, and
 * what every desktop build reports until the Rust probe target is set) must NOT
 * disable fetching, or a genuinely-online startup would freeze the session list
 * on cached/empty data. But it applies the same uniform `navigator.onLine`
 * fail-safe as {@link useDesktopConnectivity}: when the browser POSITIVELY
 * reports offline, treat startup as not-ready so query hooks seed their cached
 * data and skip a doomed fetch — otherwise an offline cold relaunch (flag OFF,
 * atom pinned at `unknown`) lands on the cached-list route but the cache is never
 * served and the list is empty. A confirmed native `online_ready_to_sync` still
 * wins over a WKWebView false-offline signal.
 */
export function useIsDesktopOnlineReady(): boolean {
  const connectivity = useAtomValue(offlineConnectivityStateAtom);
  // Subscribed rather than sampled, so a Windows transition re-runs startup
  // readiness immediately instead of waiting for an unrelated re-render.
  // Called before the non-Tauri early return to keep hook order unconditional.
  const browserIsOnline = useNavigatorOnline();

  if (!isTauri()) return true;
  // Confirmed native online wins even over a lying browser-offline signal.
  if (connectivity === 'online_ready_to_sync') return true;
  // Confirmed unreachable, OR the browser positively reports offline → not ready
  // (uniform fail-safe, flag-independent). `unknown` + browser-online stays
  // optimistic (ready) so an unarmed probe never blocks startup fetches.
  if (connectivity === 'offline' || connectivity === 'backend_unreachable') return false;
  if (!browserIsOnline) return false;
  return true;
}
